import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DateRangeFilter, endOfDayParam } from '@/components/ui/date-range-filter';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatMoney } from '@/lib/money';
import { cn } from '@/lib/utils';
import { commissionStatusLabel, commissionStatusVariant } from '../constants';
import { useCommissions, useCommissionSummary } from '../queries';
import { useDeleteCommission, useMarkCommissionPaidOut, useVoidCommission } from '../queries';
import type { Commission } from '../types';

interface CommissionsPanelProps {
  /** HR profile: scope to one employee. Omit in the cabinet (self-scoped server-side). */
  employeeId?: number;
  /** Show pay-out / void actions (COMMISSION_MANAGE_ROLES — never for the rep's own view). */
  canManage?: boolean;
}

const usd = (v: string | null | undefined) => formatMoney(v, 'USD', { narrowSymbol: true }) ?? '—';
const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

export function CommissionsPanel({ employeeId, canManage = false }: CommissionsPanelProps) {
  const navigate = useNavigate();
  // Period filter by *won date* (the natural commission timeline).
  const [wonFrom, setWonFrom] = useState('');
  const [wonTo, setWonTo] = useState('');
  const params = {
    ...(employeeId ? { employee: employeeId } : {}),
    won_after: wonFrom || undefined,
    won_before: endOfDayParam(wonTo),
  };
  const { data, isPending } = useCommissions(params);
  const { data: summary } = useCommissionSummary(params);
  const payOut = useMarkCommissionPaidOut();
  const voidIt = useVoidCommission();
  const del = useDeleteCommission();

  const rows = data?.results ?? [];

  if (isPending) return <Skeleton className="min-h-48 w-full" />;

  return (
    <div className="space-y-4">
      {/* Period filter (by deal won date). */}
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter
          label="Won"
          idPrefix="commission-won"
          from={wonFrom}
          to={wonTo}
          onFromChange={setWonFrom}
          onToChange={setWonTo}
        />
      </div>

      {/* Totals — projected (estimate) vs realized money, clearly separated. */}
      <div className="grid gap-3 sm:grid-cols-3">
        <TotalCard
          label="Pending (projected)"
          value={usd(summary?.pending_projected_base)}
          hint="Estimated — not yet earned"
          tone="muted"
        />
        <TotalCard label="Earned" value={usd(summary?.earned_base)} hint="Client paid" tone="primary" />
        <TotalCard
          label="Paid out"
          value={usd(summary?.paid_out_base)}
          hint="Paid to employee"
          tone="default"
        />
      </div>

      {/* Legend — the crux of the feature: projected until the client pays. */}
      <p className="text-muted-foreground text-xs">
        <span className="italic">Projected</span> = your rate × the deal estimate (shown while
        pending). Once the client pays, the row switches to the <strong>actual</strong> commission
        (your rate × the amount received).
      </p>

      {rows.length === 0 ? (
        <div className="text-muted-foreground flex min-h-32 items-center justify-center py-8 text-center text-sm">
          No commissions yet.
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Deal</TableHead>
                <TableHead className="hidden sm:table-cell">Client</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">Commission</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden md:table-cell">Won / Paid</TableHead>
                {canManage ? <TableHead className="w-32 text-right">Actions</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <CommissionRow
                  key={c.id}
                  commission={c}
                  canManage={canManage}
                  busy={payOut.isPending || voidIt.isPending || del.isPending}
                  onOpenDeal={() => navigate(`/crm/deals/${c.deal_public_id}`)}
                  onPayOut={() => payOut.mutate(c.public_id)}
                  onVoid={() => voidIt.mutate(c.public_id)}
                  onDelete={() => del.mutate(c.public_id)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function TotalCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint: string;
  tone: 'muted' | 'primary' | 'default';
}) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p
        className={cn(
          'text-lg font-semibold tabular-nums',
          tone === 'muted' && 'text-muted-foreground italic',
          tone === 'primary' && 'text-foreground',
        )}
      >
        {value}
      </p>
      <p className="text-muted-foreground text-[11px]">{hint}</p>
    </div>
  );
}

function CommissionRow({
  commission: c,
  canManage,
  busy,
  onOpenDeal,
  onPayOut,
  onVoid,
  onDelete,
}: {
  commission: Commission;
  canManage: boolean;
  busy: boolean;
  onOpenDeal: () => void;
  onPayOut: () => void;
  onVoid: () => void;
  onDelete: () => void;
}) {
  const isProjected = c.status === 'pending';
  const isVoid = c.status === 'void';
  // Pending → show the projected figure (muted/italic); otherwise the realized one.
  const amount = isProjected ? c.projected_base_amount : c.earned_base_amount;

  return (
    <TableRow className={cn(isVoid && 'opacity-50')}>
      <TableCell>
        <button type="button" onClick={onOpenDeal} className="font-medium hover:underline">
          {c.deal_name || '—'}
        </button>
      </TableCell>
      <TableCell className="text-muted-foreground hidden sm:table-cell">
        {c.company_name || '—'}
      </TableCell>
      <TableCell className="text-right tabular-nums">{Number(c.rate_pct)}%</TableCell>
      <TableCell className="text-right">
        <span
          className={cn(
            'tabular-nums',
            isProjected && 'text-muted-foreground italic',
            isVoid && 'line-through',
          )}
          title={isProjected ? 'Projected — estimated until the client pays' : 'Actual — from money received'}
        >
          {usd(amount)}
        </span>
        {isProjected ? (
          <Badge variant="outline" className="ml-2 text-[10px]">
            Projected
          </Badge>
        ) : null}
      </TableCell>
      <TableCell>
        <Badge variant={commissionStatusVariant(c.status)}>
          {commissionStatusLabel(c.status)}
        </Badge>
      </TableCell>
      <TableCell className="text-muted-foreground hidden md:table-cell">
        {c.status === 'paid_out' ? fmtDate(c.paid_out_date) : fmtDate(c.realized_date || c.won_date)}
      </TableCell>
      {canManage ? (
        <TableCell className="text-right">
          <div className="flex justify-end gap-2">
            {c.status === 'earned' ? (
              <Button size="sm" variant="outline" disabled={busy} onClick={onPayOut}>
                Pay out
              </Button>
            ) : null}
            {c.status !== 'void' && c.status !== 'paid_out' ? (
              <Button size="sm" variant="ghost" disabled={busy} onClick={onVoid}>
                Void
              </Button>
            ) : null}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  disabled={busy}
                  aria-label={`Delete commission for ${c.deal_name || 'deal'}`}
                  title="Delete"
                >
                  <Trash2 className="text-destructive size-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this commission?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Permanently removes the commission row. This cannot be undone — use Void to
                    cancel without deleting the record.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onDelete} disabled={busy}>
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </TableCell>
      ) : null}
    </TableRow>
  );
}
